import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// ============================================================================
// Types
// ============================================================================

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeContextType {
  /** The user's selected mode (light | dark | system) */
  mode: ThemeMode;
  /** The actual resolved theme applied to the DOM (light | dark) */
  resolvedTheme: ResolvedTheme;
  /** Update the theme mode */
  setMode: (mode: ThemeMode) => void;
}

// ============================================================================
// Constants
// ============================================================================

const THEME_STORAGE_KEY = "mutual-theme";

// ============================================================================
// Helpers
// ============================================================================

function getSystemTheme(): ResolvedTheme {
  if (typeof globalThis === "undefined") return "dark";
  return globalThis.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === "system") return getSystemTheme();
  return mode;
}

function getStoredTheme(): ThemeMode | null {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch {
    // localStorage might be unavailable
  }
  return null;
}

function storeTheme(mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // localStorage might be unavailable
  }
}

/**
 * Apply the dark class to the HTML element and update the meta theme-color.
 * This is called outside of React for flash-free initial render.
 */
function applyThemeToDOM(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  if (resolved === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  // Update meta theme-color for browser chrome
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute("content", resolved === "dark" ? "#020617" : "#ffffff");
  }
}

// ============================================================================
// Flash-free initialization script
// This runs synchronously before React mounts to prevent theme flash.
// ============================================================================
export function initializeTheme(): void {
  const stored = getStoredTheme();
  const mode = stored || "dark"; // default to dark
  const resolved = resolveTheme(mode);
  applyThemeToDOM(resolved);
}

// ============================================================================
// Context
// ============================================================================

const ThemeContext = createContext<ThemeContextType | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface ThemeProviderProps {
  children: ReactNode;
  /** Initial theme from user profile/server. Overrides localStorage on first load. */
  initialMode?: ThemeMode;
}

export function ThemeProvider({ children, initialMode }: Readonly<ThemeProviderProps>) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    // NOSONAR — lazy initializer reads localStorage for flash-free theme; arrow function is idiomatic
    // Priority: localStorage (for flash-free) > server preference > default
    return getStoredTheme() || initialMode || "dark";
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(mode));

  const initialSynced = useRef(false);

  // Sync server preference on first load (if different from localStorage)
  useEffect(() => {
    if (initialMode && !initialSynced.current) {
      initialSynced.current = true;
      const stored = getStoredTheme();
      if (!stored) {
        // No local preference — use server value
        setModeState(initialMode);
        const resolved = resolveTheme(initialMode);
        setResolvedTheme(resolved);
        applyThemeToDOM(resolved);
        storeTheme(initialMode);
      }
    }
  }, [initialMode]);

  // Listen for system preference changes when in "system" mode
  useEffect(() => {
    if (mode !== "system") return;

    const mql = globalThis.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const resolved = resolveTheme("system");
      setResolvedTheme(resolved);
      applyThemeToDOM(resolved);
    };

    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [mode]);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    const resolved = resolveTheme(newMode);
    setResolvedTheme(resolved);

    // Add transition class briefly for smooth theme switch
    document.documentElement.classList.add("theme-transition");
    applyThemeToDOM(resolved);
    setTimeout(() => {
      document.documentElement.classList.remove("theme-transition");
    }, 300);

    storeTheme(newMode);
  }, []);

  const value = useMemo<ThemeContextType>(
    () => ({
      mode,
      resolvedTheme,
      setMode,
    }),
    [mode, resolvedTheme, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
